import {
  decodeNativeDesktopResponse,
  encodeNativeDesktopRequest,
  isNativeDesktopFrameCommand,
  type NativeDesktopArguments,
} from '@crowdex/workspace-protocol';

import { electronInvoke } from './electron-bridge';
import {
  type LocalWorkspaceServerStatus,
  localWorkspaceServerStatus,
  subscribeLocalWorkspaceServerStatus,
} from './local-runtime';
import { WorkspaceRpcClient, WorkspaceTransportError } from './workspaces/workspace-rpc-client';

/** Connection details for the workspace server of the local Crowdex server. */
type LocalServer = { url: string; token: string };

/* The request/response catalogue lives in @crowdex/workspace-protocol. */

const REQUEST_TIMEOUT_MS = 120_000;
const SERVER_RESTART_TIMEOUT_MS = 20_000;

let client: { server: LocalServer; rpc: WorkspaceRpcClient<LocalServer> } | null = null;
const gitListeners = new Map<string, Set<() => void>>();
/** Roots watched on the current socket; a dropped socket loses its server-side watchers. */
const watchedGitRoots = new Set<string>();

/** Subscriptions are restored after a transport reconnect. Polling remains the fallback. */
export function subscribeNativeGitChanges(root: string, listener: () => void): () => void {
  const listeners = gitListeners.get(root) ?? new Set<() => void>();
  gitListeners.set(root, listeners);
  listeners.add(listener);
  void waitForLocalServer()
    .then((server) => {
      if (!gitListeners.has(root)) return;
      return watchGit(rpcFor(server), root);
    })
    .catch(() => undefined);
  return () => {
    listeners.delete(listener);
    if (listeners.size !== 0) return;
    gitListeners.delete(root);
    if (!watchedGitRoots.delete(root)) return;
    void client?.rpc
      .request({ type: 'workspace_git_unwatch', workspace_root: root })
      .catch(() => undefined);
  };
}

async function watchGit(rpc: WorkspaceRpcClient<LocalServer>, root: string): Promise<void> {
  if (watchedGitRoots.has(root)) return;
  watchedGitRoots.add(root);
  try {
    await rpc.request({ type: 'workspace_git_watch', workspace_root: root });
  } catch (error) {
    watchedGitRoots.delete(root);
    throw error;
  }
}

const RETRYABLE_COMMANDS = new Set([
  'fs_read_text_file',
  'fs_read_binary_file',
  'workspace_list_directory',
  'workspace_git_review',
  'workspace_checkpoint_diff',
  'workspace_git_status',
  'workspace_git_generation_context',
  'workspace_git_snapshot',
  'workspace_pull_request_cockpit',
  'workspace_environment_get',
  'acp_discover_agents',
  'acp_agent_catalog',
  'agent_terminal_output',
  'agent_terminal_read_background_output',
]);

async function waitForLocalServer(): Promise<LocalServer> {
  const current = localWorkspaceServerStatus();
  if (current === null)
    throw new WorkspaceTransportError(
      'The local workspace server is only available when Crowdex runs on this machine.',
    );
  if (current.state === 'ready') return current.server;
  if (current.state === 'failed') throw new WorkspaceTransportError(current.message);
  return new Promise<LocalServer>((resolve, reject) => {
    let unlisten: () => void = () => undefined;
    const timeout = setTimeout(() => {
      unlisten();
      reject(
        new WorkspaceTransportError('Timed out waiting for the local workspace server to restart.'),
      );
    }, SERVER_RESTART_TIMEOUT_MS);
    const settle = (status: LocalWorkspaceServerStatus) => {
      if (status.state === 'ready') {
        clearTimeout(timeout);
        unlisten();
        resolve(status.server);
      } else if (status.state === 'failed') {
        clearTimeout(timeout);
        unlisten();
        reject(new WorkspaceTransportError(status.message));
      }
    };
    unlisten = subscribeLocalWorkspaceServerStatus(settle);
    const refreshed = localWorkspaceServerStatus();
    if (refreshed !== null) settle(refreshed);
  });
}

/**
 * The shared workspace RPC client for the current CLI server. It reconnects lazily after a
 * dropped socket; a restarted server (new URL or token) gets a new client.
 */
function rpcFor(server: LocalServer): WorkspaceRpcClient<LocalServer> {
  if (client !== null && client.server.url === server.url && client.server.token === server.token)
    return client.rpc;
  client?.rpc.close();
  watchedGitRoots.clear();
  const rpc: WorkspaceRpcClient<LocalServer> = new WorkspaceRpcClient<LocalServer>({
    connection: () => ({ ...server, context: server }),
    requestTimeoutMs: REQUEST_TIMEOUT_MS,
    heartbeat: {
      intervalMs: 5_000,
      timeoutMs: 15_000,
      timeoutMessage: 'Local workspace connection stopped responding.',
    },
    messages: {
      closed: 'Local workspace connection closed.',
      connectionFailed: 'Local workspace connection failed.',
      handshakeTimeout: 'Local workspace connection handshake timed out.',
      invalidFrame: 'Local workspace server returned an invalid frame.',
      requestTimeout: 'Local workspace request timed out.',
    },
    onPush(frame) {
      if (frame.type !== 'workspace_git_changed') return;
      for (const listener of gitListeners.get(frame.workspace_root) ?? []) listener();
    },
    onReady() {
      for (const root of gitListeners.keys()) void watchGit(rpc, root).catch(() => undefined);
    },
    onDisconnect() {
      watchedGitRoots.clear();
    },
  });
  client = { server, rpc };
  return rpc;
}

/**
 * Routes shared workspace/terminal ops to the local server's workspace server. Only
 * OS/browser/secrets/plugin commands outside the frame contract use Electron IPC.
 */
export async function nativeInvoke<TResult>(
  command: string,
  args: NativeDesktopArguments = {},
): Promise<TResult> {
  if (!isNativeDesktopFrameCommand(command)) return electronInvoke<TResult>(command, args);
  const initialServer = await waitForLocalServer();
  const attempts = 2;
  let server = initialServer;
  for (let attempt = 0; attempt < attempts; attempt += 1) {
    try {
      const response = await rpcFor(server).request({
        ...encodeNativeDesktopRequest(command, args),
      });
      return decodeNativeDesktopResponse(command, response) as TResult;
    } catch (error) {
      if (!(error instanceof WorkspaceTransportError)) throw error;
      const retryable = RETRYABLE_COMMANDS.has(command);
      if (attempt + 1 >= attempts || (!retryable && error.ambiguous)) {
        throw new Error(
          !error.ambiguous
            ? error.message
            : `${error.message} The operation may have completed; refresh before trying it again.`,
        );
      }
      server = await waitForLocalServer();
    }
  }
  throw new Error('Local workspace request failed.');
}
